import type { AppKey } from './app-registry';

/**
 * Namespaced entity capability keys.
 * A key here is an opt-out clutter switch, not an entitlement (`productGate` already
 * answers "does this org have a seat in this product").
 */
export const NAMESPACED_FEATURE_KEYS = [
  // Contact
  'contact:notes',
  'contact:files',
  'contact:comments',
  // Task
  'task:files',
  'task:comments',
  'task:notes',
  // Job (Job Plan)
  'job:files',
  'job:comments',
  'job:notes',
  // Anchor (Skill Viper)
  'anchor:notes',
  'anchor:files',
  'anchor:comments',
  // Email Thread (Inbox Clinic)
  'email_thread:comments',
  'email_thread:files',
  'email_thread:notes',
  // Task mirroring into a member's own external task app (Inbox Clinic)
  'task:external_sync',
  /**
   * Inbox Clinic evidence and collaboration, rolled out as four independent controls.
   *
   * Independent on purpose: each can be turned off without the others, so a workspace
   * that wants scoped sharing but not AI routing gets exactly that, and a problem with
   * one does not force the whole change off. All opt-in — none of them should arrive
   * without somebody choosing it.
   *
   * Turning any of these off stops new behaviour and **never deletes retained evidence**.
   * Withdrawing `clinic:retained_content` stops new bodies being written; removing what
   * is already stored is a separate, audited act (`purgeWorkspaceContent`).
   */
  'clinic:retained_content',
  'clinic:scoped_grants',
  'clinic:asks',
  'clinic:semantic_workflows',
  // Global
  'workflow_ai',
] as const;

export const LEGACY_FEATURE_KEYS = ['notes', 'docs', 'chat'] as const;

export const WORKSPACE_FEATURE_KEYS = [...NAMESPACED_FEATURE_KEYS, ...LEGACY_FEATURE_KEYS] as const;

export type WorkspaceFeatureKey = (typeof WORKSPACE_FEATURE_KEYS)[number];
export type NamespacedFeatureKey = (typeof NAMESPACED_FEATURE_KEYS)[number];
export type LegacyFeatureKey = (typeof LEGACY_FEATURE_KEYS)[number];

export const DEFAULT_FEATURE_FLAG_VALUES: Record<WorkspaceFeatureKey, boolean> = {
  'contact:notes': true,
  'contact:files': true,
  'contact:comments': true,
  'task:files': true,
  'task:comments': true,
  'task:notes': false,
  'job:files': true,
  'job:comments': true,
  'job:notes': true,
  'anchor:notes': true,
  'anchor:files': true,
  'anchor:comments': true,
  'email_thread:comments': true,
  'email_thread:files': true,
  'email_thread:notes': true,
  // Off by default: mirroring reaches a member's own Microsoft account, so a
  // workspace opts in rather than discovering it already happened.
  'task:external_sync': false,
  // All four off by default. This is a rollout, and a workspace discovering that its
  // correspondence is now retained, shared, or classified is exactly the surprise these
  // controls exist to prevent.
  'clinic:retained_content': false,
  'clinic:scoped_grants': false,
  'clinic:asks': false,
  'clinic:semantic_workflows': false,
  workflow_ai: true,
  // Legacy keys default to true
  notes: true,
  docs: true,
  chat: true,
};

export const WORKSPACE_FEATURE_LABELS: Record<WorkspaceFeatureKey, string> = {
  'contact:notes': 'Contact Notes',
  'contact:files': 'Contact Files',
  'contact:comments': 'Contact Comments',
  'task:files': 'Task Files',
  'task:comments': 'Task Comments',
  'task:notes': 'Task Notes',
  'task:external_sync': 'Microsoft To Do mirroring',
  'job:files': 'Job Files',
  'job:comments': 'Job Comments',
  'job:notes': 'Job Notes',
  'anchor:notes': 'Page Notes',
  'anchor:files': 'Page Files',
  'anchor:comments': 'Page Comments',
  'email_thread:comments': 'Internal Comments',
  'email_thread:files': 'Email Files',
  'email_thread:notes': 'Thread Notes',
  'clinic:retained_content': 'Keep a copy of message content',
  'clinic:scoped_grants': 'Share a single conversation',
  'clinic:asks': 'Track what is still open',
  'clinic:semantic_workflows': 'Route attention automatically',
  workflow_ai: 'Workflow AI',
  // Legacy
  notes: 'Notes',
  docs: 'Files',
  chat: 'Workflow AI',
};

export const WORKSPACE_FEATURE_DESCRIPTIONS: Record<WorkspaceFeatureKey, string> = {
  'contact:notes': 'Rich-text background and meeting notes on contacts.',
  'contact:files': 'Document and image attachments on contacts.',
  'contact:comments': 'Internal team discussion on contacts.',
  'task:files': 'Document and file attachments on tasks.',
  'task:comments': 'Team discussion and progress updates on tasks.',
  'task:notes': 'Dedicated rich-text scratchpad on tasks.',
  'task:external_sync':
    "Send a member's own tasks to a list in their Microsoft To Do, and read completions back.",
  'job:files': 'Site photos and proof-of-work uploads on jobs.',
  'job:comments': 'Field-to-dispatch communication and updates on jobs.',
  'job:notes': 'Service logs and technical handover on jobs.',
  'anchor:notes': 'Notes and memos attached to saved web pages.',
  'anchor:files': 'Files and downloads saved to web pages.',
  'anchor:comments': 'Pinned annotations and discussion on web pages.',
  'email_thread:comments': 'Private internal team discussion on email conversations.',
  'email_thread:files': 'Attachments and documents attached to email threads.',
  'email_thread:notes': 'Triage memos and customer context on email threads.',
  'clinic:retained_content':
    'Keep the text of messages, so a conversation stays readable after the mail provider stops returning it. Turning this off stops new copies being kept and does not remove what is already here.',
  'clinic:scoped_grants':
    'Give someone access to one conversation — to read it, discuss it, or draft a reply — without opening the rest of the mailbox.',
  'clinic:asks':
    'Track the individual requests inside a conversation, each linked to the message it came from, so nothing gets lost in a long thread.',
  'clinic:semantic_workflows':
    'Read conversations to work out what is waiting on whom. Needs a copy of message content, and sends it to a model provider.',
  workflow_ai: 'Contextual intelligence, auto-extraction, summarization, and in-page assistance.',
  // Legacy
  notes: 'Free-form notes on anchors.',
  docs: 'Uploaded files attached to anchors.',
  chat: 'In-page intelligence and workflow assistance.',
};

export type ContactRelationTab = 'anchors' | 'jobs' | 'emails';
export type ActivityFeedCategory =
  | 'task'
  | 'note'
  | 'file'
  | 'meeting'
  | 'anchor'
  | 'job'
  | 'email';

export interface FeatureSection {
  title: string;
  description: string;
  keys: readonly NamespacedFeatureKey[];
}

export interface AppPersonaConfig {
  supportedRelationTabs: readonly ContactRelationTab[];
  candidateFeatureKeys: readonly NamespacedFeatureKey[];
  activityCategories: readonly ActivityFeedCategory[];
  entitySections: readonly FeatureSection[];
}

export const APP_PERSONAS: Record<AppKey, AppPersonaConfig> = {
  'skill-viper': {
    supportedRelationTabs: ['anchors'],
    candidateFeatureKeys: [
      'contact:notes',
      'contact:files',
      'contact:comments',
      'task:files',
      'task:comments',
      'task:notes',
      'anchor:notes',
      'anchor:files',
      'anchor:comments',
      'workflow_ai',
    ],
    activityCategories: ['task', 'note', 'file', 'meeting', 'anchor'],
    entitySections: [
      {
        title: 'Contact Features',
        description: 'Capabilities available on contacts.',
        keys: ['contact:notes', 'contact:files', 'contact:comments'],
      },
      {
        title: 'Task Features',
        description: 'Capabilities available on tasks.',
        keys: ['task:files', 'task:comments', 'task:notes'],
      },
      {
        title: 'Page & Anchor Features',
        description: 'Capabilities attached to saved web pages.',
        keys: ['anchor:notes', 'anchor:files', 'anchor:comments'],
      },
      {
        title: 'Intelligence & Automation',
        description: 'Platform intelligence and workflow assistance.',
        keys: ['workflow_ai'],
      },
    ],
  },
  'job-plan': {
    supportedRelationTabs: ['jobs'],
    candidateFeatureKeys: [
      'contact:notes',
      'contact:files',
      'contact:comments',
      'task:files',
      'task:comments',
      'task:notes',
      'job:files',
      'job:comments',
      'job:notes',
      'workflow_ai',
    ],
    activityCategories: ['task', 'note', 'file', 'meeting', 'job'],
    entitySections: [
      {
        title: 'Contact Features',
        description: 'Capabilities available on contacts.',
        keys: ['contact:notes', 'contact:files', 'contact:comments'],
      },
      {
        title: 'Task Features',
        description: 'Capabilities available on tasks.',
        keys: ['task:files', 'task:comments', 'task:notes'],
      },
      {
        title: 'Job Features',
        description: 'Capabilities available on scheduled service jobs.',
        keys: ['job:files', 'job:comments', 'job:notes'],
      },
      {
        title: 'Intelligence & Automation',
        description: 'Platform intelligence and workflow assistance.',
        keys: ['workflow_ai'],
      },
    ],
  },
  'inbox-clinic': {
    supportedRelationTabs: ['emails'],
    candidateFeatureKeys: [
      'contact:notes',
      'contact:files',
      'contact:comments',
      'task:files',
      'task:comments',
      'task:notes',
      'email_thread:comments',
      'email_thread:files',
      'email_thread:notes',
      'workflow_ai',
    ],
    activityCategories: ['task', 'note', 'file', 'meeting', 'email'],
    entitySections: [
      {
        title: 'Contact Features',
        description: 'Capabilities available on contacts.',
        keys: ['contact:notes', 'contact:files', 'contact:comments'],
      },
      {
        title: 'Task Features',
        description: 'Capabilities available on tasks.',
        keys: ['task:files', 'task:comments', 'task:notes'],
      },
      {
        title: 'Email Thread Features',
        description: 'Capabilities attached to customer email conversations.',
        keys: ['email_thread:comments', 'email_thread:files', 'email_thread:notes'],
      },
      {
        title: 'Intelligence & Automation',
        description: 'Platform intelligence and workflow assistance.',
        keys: ['workflow_ai'],
      },
    ],
  },
  'real-estate-studio': {
    supportedRelationTabs: [],
    candidateFeatureKeys: [
      'contact:notes',
      'contact:files',
      'contact:comments',
      'task:files',
      'task:comments',
      'task:notes',
      'workflow_ai',
    ],
    activityCategories: ['task', 'note', 'file', 'meeting'],
    entitySections: [
      {
        title: 'Contact Features',
        description: 'Capabilities available on contacts.',
        keys: ['contact:notes', 'contact:files', 'contact:comments'],
      },
      {
        title: 'Task Features',
        description: 'Capabilities available on tasks.',
        keys: ['task:files', 'task:comments', 'task:notes'],
      },
      {
        title: 'Intelligence & Automation',
        description: 'Platform intelligence and workflow assistance.',
        keys: ['workflow_ai'],
      },
    ],
  },
};

export function getAppPersona(appKey?: string | null): AppPersonaConfig {
  if (appKey && appKey in APP_PERSONAS) {
    return APP_PERSONAS[appKey as AppKey];
  }
  return APP_PERSONAS['skill-viper'];
}
